import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from '@mui/material';
import {useValidateDownload} from '../../../hooks/productions/useProductions';
import {ValidateDownloadRequest} from '../../../api/types/production.type';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import '../Demo.scss';

const ValidateDownloadApiTest: React.FC = () => {
  const [key, setKey] = useState<string>(
    'dFdMYnZaU2xtaGpUR2dndmdFalJtQVNldUs4dkFJK0wwMXdjT3UzSzNkbGFnd3dXNGx4dmlnPT0='
  );
  const [passcode, setPasscode] = useState<string>('8DFf1F8qilD+KS3xnwQrVA==');
  const [type, setType] = useState<string>('A');
  const [validateLinkIpAdd, setValidateLinkIpAdd] = useState<string>('demo-ip');
  const [validateLinkAbsoluteUri, setValidateLinkAbsoluteUri] =
    useState<string>(
      'https://aurora-review-dev.consiliotest.com/prod/dFdMYnZaU2xtaGpUR2dndmdFalJtQVNldUs4dkFJK0wwMXdjT3UzSzNkbGFnd3dXNGx4dmlnPT0='
    );
  const [queryParam, setQueryParam] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const validateDownloadMutation = useValidateDownload(
    (data) => {
      console.log('Download validated successfully:', data);
    },
    (error) => {
      console.error('Error validating download:', error);
    }
  );

  const handleTestApi = () => {
    const payload: ValidateDownloadRequest = {
      key,
      passcode,
      type,
      validateLinkIpAdd,
      validateLinkAbsoluteUri,
    };
    validateDownloadMutation.mutate({
      payload,
      queryParam: queryParam || undefined,
    });
  };

  const handleReset = () => {
    setKey(
      'dFdMYnZaU2xtaGpUR2dndmdFalJtQVNldUs4dkFJK0wwMXdjT3UzSzNkbGFnd3dXNGx4dmlnPT0='
    );
    setPasscode('8DFf1F8qilD+KS3xnwQrVA==');
    setType('A');
    setValidateLinkIpAdd('demo-ip');
    setValidateLinkAbsoluteUri(
      'https://aurora-review-dev.consiliotest.com/prod/dFdMYnZaU2xtaGpUR2dndmdFalJtQVNldUs4dkFJK0wwMXdjT3UzSzNkbGFnd3dXNGx4dmlnPT0='
    );
    setQueryParam('');
  };

  const handleCopyJson = async () => {
    const payload = {
      key,
      passcode,
      type,
      validateLinkIpAdd,
      validateLinkAbsoluteUri,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🔐 Validate Download API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Validate and download a production file using key, passcode, and
        validation parameters
      </Typography>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip
              data-testid={`validate-download-api-chip-success`}
              label="POST"
              color="success"
              size="small"
            />
            <Typography variant="h6" className="typo-text subhead2_size">
              Validate Download
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/download/validate-download
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Validate download link and download the production file. Returns a
            binary file response.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Payload */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Payload
        </Typography>

        <Box
          sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2}}
        >
          <TextField
            label="Key"
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            size="small"
            placeholder="Enter key"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Passcode"
            type="text"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            size="small"
            placeholder="Enter passcode"
            fullWidth
          />
          <TextField
            label="Type"
            type="text"
            value={type}
            onChange={(e) => setType(e.target.value)}
            size="small"
            placeholder="Enter type (e.g., A)"
            sx={{maxWidth: '200px'}}
          />
          <TextField
            label="Validate Link IP Address"
            type="text"
            value={validateLinkIpAdd}
            onChange={(e) => setValidateLinkIpAdd(e.target.value)}
            size="small"
            placeholder="Enter IP address"
            fullWidth
          />
          <TextField
            label="Validate Link Absolute URI"
            type="text"
            value={validateLinkAbsoluteUri}
            onChange={(e) => setValidateLinkAbsoluteUri(e.target.value)}
            size="small"
            placeholder="Enter absolute URI"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Query Parameter (Optional)"
            type="text"
            value={queryParam}
            onChange={(e) => setQueryParam(e.target.value)}
            size="small"
            placeholder="Leave empty or enter query param name"
            helperText="Optional query parameter name (e.g., 'param')"
            sx={{maxWidth: '400px'}}
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            disabled={validateDownloadMutation.isPending}
          >
            {validateDownloadMutation.isPending
              ? '⏳ Validating & Downloading...'
              : '🚀 Validate & Download'}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            🔄 Reset
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {validateDownloadMutation.isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Validating download and downloading file... Making API request...
          </Typography>
          <LinearProgress sx={{marginTop: 2}} />
        </Box>
      )}

      {/* Error State */}
      {validateDownloadMutation.error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {validateDownloadMutation.error.message}
          </Typography>
        </Box>
      )}

      {/* Success State */}
      {validateDownloadMutation.data && !validateDownloadMutation.isPending && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ Download Successful
          </Typography>

          {/* Success Message */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size" gutterBottom>
              🎉 File Downloaded Successfully!
            </Typography>
            <Typography className="typo-text body3_size" gutterBottom>
              <strong>Filename:</strong>{' '}
              {validateDownloadMutation.data.filename}
            </Typography>
            <Typography className="typo-text body3_size">
              <strong>Content Type:</strong>{' '}
              {validateDownloadMutation.data.contentType}
            </Typography>
            <Typography className="typo-text body3_size">
              <strong>File Size:</strong>{' '}
              {(validateDownloadMutation.data.blob.size / 1024).toFixed(2)} KB
            </Typography>
          </Box>

          {/* Expandable Sections */}
          <Box sx={{marginTop: 2}}>
            {/* Request Payload */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📤 Request Payload
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyJson}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Payload'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(
                    {
                      key,
                      passcode,
                      type,
                      validateLinkIpAdd,
                      validateLinkAbsoluteUri,
                    },
                    null,
                    2
                  )}
                </pre>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      )}

      {/* Initial State */}
      {!validateDownloadMutation.isPending &&
        !validateDownloadMutation.data &&
        !validateDownloadMutation.error && (
          <Box
            sx={{
              padding: 3,
              backgroundColor: '#fff3e0',
              borderRadius: 1,
              border: '1px solid #ff9800',
            }}
          >
            <Typography className="typo-text body2_size">
              👆 Fill in the form and click "Validate & Download" button to make
              the API request
            </Typography>
          </Box>
        )}

      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/download/validate-download
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> POST
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Content-Type:</strong> application/json
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Request Body:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {JSON.stringify(
            {
              key,
              passcode,
              type,
              validateLinkIpAdd,
              validateLinkAbsoluteUri,
            },
            null,
            2
          )}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Response:</strong> Binary file (blob) - automatically triggers
          download
        </Typography>
      </Box>

      {/* Hook Usage Example */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#f0f8ff'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          💻 Hook Usage Example
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {`import { useValidateDownload } from '../../../hooks/productions/useProductions';
import { ValidateDownloadRequest } from '../../../api/types/production.type';

const MyComponent = () => {
  const validateDownload = useValidateDownload(
    (data) => {
      console.log('Download successful:', data.filename);
      // File is automatically downloaded
    },
    (error) => console.error('Error:', error)
  );

  const handleDownload = () => {
    const payload: ValidateDownloadRequest = {
      key: 'dFdMYnZaU2xtaGpUR2dndmdFalJtQVNldUs4dkFJK0wwMXdjT3UzSzNkbGFnd3dXNGx4dmlnPT0=',
      passcode: '8DFf1F8qilD+KS3xnwQrVA==',
      type: 'A',
      validateLinkIpAdd: '192.168.0.1',
      validateLinkAbsoluteUri: 'https://aurora-review-dev.consiliotest.com/prod/...'
    };
    
    validateDownload.mutate({ payload });
  };

  return (
    <button 
      onClick={handleDownload}
      disabled={validateDownload.isPending}
    >
      {validateDownload.isPending ? 'Downloading...' : 'Download File'}
    </button>
  );
};`}
        </pre>
      </Box>
    </Box>
  );
};

export default ValidateDownloadApiTest;
